import { useState } from "react";
import { Link } from "react-router-dom";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import PageTitle from "@/components/base/PageTitle";
import ShareCardsGallery from "./components/ShareCardsGallery";

const collections = [
  {
    name: "Round Brilliant",
    tagline: "The Classic Standard",
    desc: "The most popular diamond shape, engineered for maximum fire and brilliance with 58 precisely cut facets. Timeless, elegant, and universally admired.",
    image: "https://readdy.ai/api/search-image?query=stunning%20round%20brilliant%20cut%20diamond%20solitaire%20ring%20on%20dark%20black%20velvet%20surface%2C%20classic%20gold%20six%20prong%20setting%2C%20sparkling%20facets%2C%20luxury%20jewelry%20photography%2C%20dramatic%20lighting%2C%20ultra%20high%20detail%2C%20professional%20studio%20shot&width=500&height=620&seq=col-round&orientation=portrait",
    specs: { "Cut": "58 Facets", "Brilliance": "Maximum", "Best For": "Classic Elegance" },
  },
  {
    name: "Oval",
    tagline: "Elongated Grace",
    desc: "A sophisticated variation of the round brilliant with an elongated silhouette. Creates the illusion of greater size while offering exceptional sparkle and a flattering, slenderizing effect on the finger.",
    image: "https://readdy.ai/api/search-image?query=oval%20cut%20diamond%20solitaire%20engagement%20ring%20in%20gold%20setting%20on%20dark%20black%20velvet%20surface%2C%20elongated%20elegant%20shape%2C%20sparkling%20brilliant%20facets%2C%20luxury%20fine%20jewelry%20photography%2C%20dramatic%20studio%20lighting%2C%20ultra%20high%20detail&width=500&height=620&seq=col-oval&orientation=portrait",
    specs: { "Cut": "Modified Brilliant", "Brilliance": "High", "Best For": "Elongated Elegance" },
  },
  {
    name: "Emerald",
    tagline: "Step-Cut Sophistication",
    desc: "Distinguished by its rectangular step-cut facets and cropped corners, the emerald cut emphasizes clarity over brilliance. Its hall-of-mirrors effect creates a mesmerizing, understated elegance.",
    image: "https://readdy.ai/api/search-image?query=emerald%20cut%20diamond%20engagement%20ring%20in%20gold%20setting%20on%20dark%20black%20velvet%20surface%2C%20rectangular%20step%20cut%20facets%2C%20hall%20of%20mirrors%20effect%2C%20luxury%20fine%20jewelry%20photography%2C%20dramatic%20lighting%2C%20ultra%20high%20detail%2C%20professional%20studio%20shot&width=500&height=620&seq=col-emerald&orientation=portrait",
    specs: { "Cut": "Step Cut", "Brilliance": "Subtle", "Best For": "Understated Luxury" },
  },
  {
    name: "Princess",
    tagline: "Modern Angular Fire",
    desc: "A square or rectangular shape with pointed corners and a brilliant facet pattern. The princess cut delivers exceptional fire and scintillation in a contemporary, geometric silhouette.",
    image: "https://readdy.ai/api/search-image?query=princess%20cut%20diamond%20solitaire%20ring%20in%20gold%20setting%20on%20dark%20black%20velvet%20surface%2C%20square%20shape%20with%20pointed%20corners%2C%20brilliant%20facets%20sparkling%2C%20luxury%20jewelry%20photography%2C%20dramatic%20studio%20lighting%2C%20ultra%20high%20detail&width=500&height=620&seq=col-princess&orientation=portrait",
    specs: { "Cut": "Brilliant Square", "Brilliance": "Maximum", "Best For": "Modern Statement" },
  },
  {
    name: "Cushion",
    tagline: "Romantic Vintage Charm",
    desc: "A square or rectangular shape with softly rounded corners, resembling a pillow. The cushion cut blends vintage romance with modern brilliance, offering a warm, romantic glow.",
    image: "https://readdy.ai/api/search-image?query=distinctive%20cushion%20cut%20diamond%20engagement%20ring%2C%20square%20shape%20with%20prominently%20rounded%20soft%20pillow-like%20corners%2C%20gentle%20curved%20edges%2C%20brilliant%20cut%20facets%2C%20gold%20prong%20setting%2C%20centered%20on%20dark%20black%20velvet%20surface%2C%20luxury%20fine%20jewelry%20photography%2C%20dramatic%20studio%20lighting%2C%20ultra%20high%20detail%2C%20professional%20shot%20emphasizing%20the%20uniquely%20rounded%20square%20silhouette&width=500&height=620&seq=col-cushion-v2&orientation=portrait",
    specs: { "Cut": "Modified Brilliant", "Brilliance": "Warm", "Best For": "Vintage Romance" },
  },
  {
    name: "Pear",
    tagline: "Teardrop Distinction",
    desc: "A unique hybrid combining the rounded base of a round brilliant with the tapered point of a marquise. The pear shape is distinctive, dramatic, and elongates the finger with graceful asymmetry.",
    image: "https://readdy.ai/api/search-image?query=pear%20cut%20diamond%20teardrop%20shape%20solitaire%20ring%20in%20gold%20setting%20on%20dark%20black%20velvet%20surface%2C%20rounded%20base%20with%20tapered%20point%2C%20unique%20dramatic%20shape%2C%20luxury%20jewelry%20photography%2C%20sparkling%20facets%2C%20dramatic%20lighting%2C%20ultra%20high%20detail&width=500&height=620&seq=col-pear&orientation=portrait",
    specs: { "Cut": "Hybrid Brilliant", "Brilliance": "High", "Best For": "Unique Statement" },
  },
  {
    name: "Marquise",
    tagline: "Regal Elongation",
    desc: "An elongated shape with pointed ends, inspired by a smile. The marquise cut maximizes carat weight appearance, creating the illusion of a significantly larger stone on the finger.",
    image: "https://readdy.ai/api/search-image?query=marquise%20cut%20diamond%20solitaire%20ring%20in%20gold%20setting%20on%20dark%20black%20velvet%20surface%2C%20elongated%20shape%20with%20pointed%20ends%2C%20dramatic%20regal%20style%2C%20sparkling%20brilliant%20facets%2C%20luxury%20jewelry%20photography%2C%20dramatic%20lighting%2C%20ultra%20high%20detail&width=500&height=620&seq=col-marquise&orientation=portrait",
    specs: { "Cut": "Modified Brilliant", "Brilliance": "High", "Best For": "Maximum Size" },
  },
  {
    name: "Asscher",
    tagline: "Art Deco Precision",
    desc: "A square step-cut with deeply cropped corners, creating an almost octagonal outline. The Asscher cut features concentric square facets that draw the eye inward, evoking Art Deco glamour.",
    image: "https://readdy.ai/api/search-image?query=asscher%20cut%20diamond%20solitaire%20ring%20in%20gold%20setting%20on%20dark%20black%20velvet%20surface%2C%20square%20step%20cut%20with%20cropped%20corners%2C%20art%20deco%20style%2C%20hall%20of%20mirrors%20facets%2C%20luxury%20jewelry%20photography%2C%20dramatic%20lighting%2C%20ultra%20high%20detail&width=500&height=620&seq=col-asscher&orientation=portrait",
    specs: { "Cut": "Step Cut", "Brilliance": "Subtle", "Best For": "Art Deco Glamour" },
  },
  {
    name: "Radiant",
    tagline: "Brilliant Square Fusion",
    desc: "A square or rectangular shape combining the elegant outline of an emerald with the fiery brilliance of a round. The radiant cut offers the best of both worlds — geometric sophistication with exceptional sparkle.",
    image: "https://public.readdy.ai/ai/img_res/edited_55ee7173cbcdc1a243c81be75f25daaf_6a8851a7.jpg",
    specs: { "Cut": "Brilliant Rectangular", "Brilliance": "Maximum", "Best For": "Bold Elegance" },
  },
];

function DownloadButton({ imageUrl, fileName }: { imageUrl: string; fileName: string }) {
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await fetch(imageUrl);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `${fileName.replace(/\s+/g, "_")}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch {
      window.open(imageUrl, "_blank");
    }
    setDownloading(false);
  };

  return (
    <button
      onClick={handleDownload}
      disabled={downloading}
      className="absolute top-3 right-3 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-zayan-bg/70 border border-zayan-gold/40 text-zayan-gold hover:bg-zayan-gold hover:text-zayan-bg hover:border-zayan-gold transition-all duration-300 cursor-pointer"
      aria-label={`Download ${fileName}`}
      title={`Download ${fileName}`}
    >
      {downloading ? (
        <i className="ri-loader-4-line animate-spin text-sm" />
      ) : (
        <i className="ri-download-line text-sm" />
      )}
    </button>
  );
}

export default function Collections() {
  const heroRef = useScrollReveal<HTMLDivElement>();
  const gridRef = useScrollReveal<HTMLDivElement>();
  const ctaRef = useScrollReveal<HTMLDivElement>();

  return (
    <div className="min-h-screen bg-zayan-bg flex flex-col">
      <PageTitle title="Collections - Zayan Atelier" />
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-[130px] md:pt-[170px] overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[60vh]">
          <div className="flex flex-col justify-center px-8 lg:px-16 py-16 lg:py-24 reveal" ref={heroRef}>
            <div className="flex items-center gap-3 mb-6">
              <span className="text-zayan-gold text-xs tracking-[0.2em] font-sans">DIAMOND COLLECTIONS</span>
              <div className="h-px w-12 bg-zayan-gold" />
            </div>
            <h2 className="font-serif text-4xl lg:text-6xl text-zayan-gold leading-tight mb-6">
              Explore Diamond Shapes
            </h2>
            <div className="h-px w-16 bg-zayan-gold mb-8" />
            <p className="text-zayan-textMuted text-sm leading-relaxed max-w-md">
              Every diamond shape tells a different story. Discover the distinct character, brilliance, and elegance of each cut — all sourced with IGI certification and personalized guidance.
            </p>
          </div>

          {/* Right Image */}
          <div className="relative overflow-hidden h-64 sm:h-80 lg:h-auto">
            <img
              src="https://readdy.ai/api/search-image?query=multiple%20loose%20diamonds%20of%20various%20cuts%20including%20round%20oval%20emerald%20princess%20cushion%20arranged%20on%20dark%20black%20velvet%20surface%2C%20luxury%20diamond%20collection%20photography%2C%20dramatic%20lighting%2C%20sparkling%20facets%2C%20ultra%20high%20detail%2C%20editorial%20style%20composition&width=700&height=700&seq=col-hero&orientation=portrait"
              alt="Diamond collections"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-zayan-bg/60 to-transparent" />
          </div>
        </div>
      </section>

      {/* Collections Grid */}
      <section className="border-t border-zayan-border py-16 md:py-20 lg:py-24 px-6 lg:px-16">
        <div className="max-w-7xl mx-auto reveal" ref={gridRef}>
          <div className="text-center mb-12 md:mb-16">
            <p className="text-zayan-gold text-xs tracking-[0.2em] uppercase mb-3 font-sans">
              Nine Signature Cuts
            </p>
            <h2 className="font-serif text-3xl md:text-4xl text-zayan-gold mb-4">
              Shape Your Story
            </h2>
            <p className="text-zayan-textMuted text-sm max-w-xl mx-auto">
              Each shape offers a unique interplay of light, proportion, and personality. Select the cut that reflects your individual style.
            </p>
          </div>

          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6"
            data-product-shop
          >
            {collections.map((c, i) => (
              <div
                key={i}
                className="group border border-zayan-border hover:border-zayan-gold/30 transition-all duration-500"
              >
                {/* Image */}
                <div className="relative overflow-hidden h-72 md:h-80">
                  <img
                    src={c.image}
                    alt={`${c.name} diamond`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-zayan-bg/80 via-transparent to-transparent" />
                  <DownloadButton imageUrl={c.image} fileName={c.name} />
                  <div className="absolute bottom-4 left-4 right-4">
                    <p className="text-zayan-gold text-xs tracking-[0.15em] font-sans mb-0.5">
                      {c.tagline}
                    </p>
                    <h3 className="font-serif text-xl text-zayan-gold">
                      {c.name}
                    </h3>
                  </div>
                </div>

                {/* Content */}
                <div className="p-5 md:p-6">
                  <p className="text-zayan-textMuted text-sm leading-relaxed mb-5">
                    {c.desc}
                  </p>

                  {/* Specs */}
                  <div className="border-t border-zayan-border pt-4">
                    <div className="grid grid-cols-3 gap-2">
                      {Object.entries(c.specs).map(([k, v]) => (
                        <div key={k} className="text-center">
                          <p className="text-zayan-textDarker text-[10px] tracking-wider uppercase mb-0.5">
                            {k}
                          </p>
                          <p className="text-zayan-gold text-xs font-medium">
                            {v}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Shareable Cards Gallery */}
      <ShareCardsGallery />

      {/* CTA Banner */}
      <section className="border-t border-zayan-border bg-zayan-bgCard px-6 lg:px-16 py-16">
        <div className="max-w-4xl mx-auto text-center reveal" ref={ctaRef}>
          <div className="w-14 h-14 flex items-center justify-center rounded-full border border-zayan-gold/50 mx-auto mb-6">
            <i className="ri-vip-diamond-line text-zayan-gold text-xl" />
          </div>
          <h2 className="font-serif text-3xl lg:text-4xl text-zayan-gold mb-4">
            Ready to Find Your Diamond?
          </h2>
          <p className="text-zayan-textMuted text-sm leading-relaxed max-w-lg mx-auto mb-8">
            Tell us your preferred shape, size, and budget. Zayan Atelier will curate IGI-certified options tailored to your exact specifications.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/request-a-diamond"
              className="border border-zayan-gold bg-zayan-gold/10 text-zayan-gold px-8 py-3 text-xs tracking-[0.15em] hover:bg-zayan-gold hover:text-zayan-bg transition-colors text-center whitespace-nowrap"
            >
              BEGIN YOUR SEARCH
            </Link>
            <Link
              to="/contact"
              className="border border-zayan-gold/50 text-zayan-textMuted px-8 py-3 text-xs tracking-[0.15em] hover:border-zayan-gold hover:text-zayan-gold transition-colors text-center whitespace-nowrap"
            >
              SCHEDULE CONSULTATION
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}